import React from 'react';
import { Box, Card, CardActionArea, CardContent, List, Typography } from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import { useAuth } from '../contexts/AuthContext';

const REPORT_ITEMS = [
  {
    id: 'sponsors',
    label: 'Sponsors Report',
    description: 'Unique sponsors with sponsorship and paid totals by product/variation.',
    path: '/reports/sponsors',
    perms: ['sponsors_reports:access', 'reports:view'],
  },
  {
    id: 'event',
    label: 'Event Reports',
    description: 'Event order line report with filters and export.',
    path: '/reports/event-reports',
    perms: ['event_reports:access', 'reports:view'],
  },
];

function canAccess(perms, role, requiredPerms) {
  if (String(role || '').toLowerCase() === 'admin') return true;
  if (Array.isArray(perms) && perms.includes('admin:full')) return true;
  return requiredPerms.some((p) => perms.includes(p));
}

export default function ReportsPage() {
  const navigate = useNavigate();
  const { staff } = useAuth();
  const perms = Array.isArray(staff?.permissions) ? staff.permissions : [];
  const visible = REPORT_ITEMS.filter((item) => canAccess(perms, staff?.role, item.perms));

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Reports</Typography>
        <Typography variant="body2" color="text.secondary">
          View and export ICCT reporting data.
        </Typography>
      </Box>

      <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {visible.map((item) => (
          <Card
            key={item.id}
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              '&:hover': {
                boxShadow: 2,
                borderColor: 'primary.main',
              },
            }}
          >
            <CardActionArea onClick={() => navigate(item.path)} sx={{ display: 'block' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </Box>
                <ChevronRightIcon color="action" />
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
        {visible.length === 0 ? (
          <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No report access assigned. Enable `Event Reports` or `Sponsors Report` permission for this role/user.
            </Typography>
          </Box>
        ) : null}
      </List>
    </PageLayout>
  );
}

